import {
  HStack,
  Text,
  chakra,
  VStack,
  useToast,
  useBreakpointValue,
} from "@chakra-ui/react";

import { AdminTicketType } from "@common/interfaces/KYDTicket";
import { TieredTicketTypes, showErrorToast } from "../../../utils";
import { Reorder } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import TicketTypeDraggable from "./TicketTypeDraggable";
import actions from "../../../actions";
import {
  KYDAdminEvent,
  KYDEvent,
  KYDEventRealtimeMetrics,
} from "@common/interfaces/KYDEvent";

const List = chakra(Reorder.Group);

const TableHeading = ({
  children = <></>,
  ...props
}: {
  children?: React.ReactNode;
  minW?: string;
}) => {
  const fontSize = useBreakpointValue({ base: "xs", md: "small" });

  return (
    <Text
      display="table-cell"
      textTransform={"uppercase"}
      color="gray.600"
      fontSize={fontSize}
      minWidth="100px"
      letterSpacing={"wider"}
      fontWeight={"bold"}
      whiteSpace={{ base: "normal", md: "nowrap" }}
      lineHeight={{ base: "1.2", md: "normal" }}
      py={{ base: 1, md: 0 }}
      {...props}
    >
      {children}
    </Text>
  );
};

export const TicketTypesTable = ({
  eventId,
  ticketTypes,
  tiers,
  onEdit,
  isLoading,
  readOnly = false,
  isDisabled = false,
  realtimeMetrics,
  ...props
}: {
  eventId: KYDEvent["id"];
  readOnly: boolean;
  ticketTypes?: AdminTicketType[];
  tiers: TieredTicketTypes;
  onEdit: (ticketType: AdminTicketType) => void;
  realtimeMetrics?: KYDEventRealtimeMetrics;
  isLoading?: boolean;
  isDisabled?: boolean;
}) => {
  const [ticketTypesOrder, setTicketTypesOrder] = useState<string[]>([]);
  const ticketTypeOrderRef = useRef(null);
  const toast = useToast();
  const headerHeight = useBreakpointValue({ base: "auto", md: "32px" });

  useEffect(() => {
    setTicketTypesOrder(ticketTypes?.map((p) => p.id));
  }, [ticketTypes, setTicketTypesOrder]);

  useEffect(() => {
    //@ts-ignore
    ticketTypeOrderRef.current = ticketTypesOrder;
  }, [ticketTypesOrder]);

  const onUpdateOrder = async () => {
    const newOrder = ticketTypeOrderRef.current;

    const ticketTypesInNewOrder = newOrder?.map((no) =>
      ticketTypes.find((p) => p.id === no)
    );

    const ticket_type_ids = ticketTypesInNewOrder?.map((tt) => tt.id);

    try {
      await actions.ADMIN.updateEventSync(
        eventId,
        "ticket_type_display_order",
        { ticket_type_ids }
      );
    } catch (err) {
      showErrorToast(toast, err.message);
    }
  };

  return (
    <VStack
      {...props}
      display="table"
      gap={4}
      w="full"
      sx={{
        mt: "16px",
        opacity: isDisabled ? 0.5 : 1,
        borderCollapse: "collapse",
      }}
    >
      <HStack
        w="full"
        display={"table-row"}
        borderBottomWidth={1}
        height={headerHeight}
        borderBottomColor={"gray.100"}
        alignItems="center"
      >
        <Text display={{ base: "none", md: "table-cell" }} minW="32px"></Text>
        <TableHeading>Name</TableHeading>
        <TableHeading minW="90px">Price</TableHeading>
        <TableHeading minW="90px">Sold</TableHeading>
        <TableHeading minW="110px">Revenue</TableHeading>
        <TableHeading minW="70px">Status</TableHeading>
        <TableHeading minW="90px"></TableHeading>
      </HStack>
      {ticketTypesOrder ? (
        <List
          axis="y"
          onReorder={setTicketTypesOrder}
          values={ticketTypesOrder}
          listStyleType={"none"}
          draggable={false}
          display={"table-row-group"}
        >
          {ticketTypesOrder
            .map((no) => ticketTypes.find((tt) => tt.id === no))
            .map(
              (ticketType: AdminTicketType) =>
                ticketType && (
                  <TicketTypeDraggable
                    readOnly={readOnly}
                    ticketType={ticketType}
                    tiers={tiers}
                    onUpdate={onUpdateOrder}
                    key={ticketType.id}
                    onEdit={onEdit}
                    isLoading={isLoading}
                    realtimeMetrics={realtimeMetrics}
                  />
                )
            )}
        </List>
      ) : null}

      {ticketTypes?.length === 0 ? (
        <Text py={4} color="gray.500">
          No ticket types created yet
        </Text>
      ) : null}
      {/* </VStack> */}
    </VStack>
  );
};
